import { useEffect, useRef, useState } from 'react';
import { episodeFromTake } from '../lib/cutEpisode';
import { type Card, api, type Brand, type Source, type SourceKind, type DemoChapter, type Turn } from '../lib/api';
import { AddTakeToVideo, SourcePlayer, SourceThumb } from './Record';
import { CameraCapture } from './CameraCapture';

// Not everyone wants an avatar. A listing agent has walkthrough footage, an
// interviewer has two cameras and a lapel mic, an archivist has a shelf of
// tapes. Each of those is a source, and the Flight Deck cuts to them the same
// way it cuts to a generated panel.
const KINDS: { id: SourceKind; label: string; hint: string; live: boolean }[] = [
  { id: 'file',       label: 'Video file',  hint: 'MP4, MOV, AVI, MKV — anything ffmpeg reads', live: false },
  { id: 'tape',       label: 'Tape / DVD',  hint: 'VHS or DVD capture — deinterlaced and de-squeezed on ingest', live: false },
  { id: 'camera',     label: 'Camera',      hint: 'A live feed pushed over RTMP or SRT — a camera plugged in here is captured under Connected cameras', live: true },
  { id: 'microphone', label: 'Audio',       hint: 'A live audio-only feed', live: true },
  { id: 'screen',     label: 'Screen share',hint: 'A shared screen or SONAR capture', live: true },
  { id: 'stream',     label: 'Stream',      hint: 'Any other live ingest', live: true },
];

const STATUS_TONE: Record<Source['status'], string> = {
  ready: 'var(--green)', live: 'var(--green)', ingesting: 'var(--amber)',
  pending: 'var(--ink-3)', offline: 'var(--ink-3)', error: 'var(--red)',
};

function describe(s: Source): string {
  if (s.mode === 'live') {
    // "pending" on its own reads as "something is wrong". A live source is
    // waiting on the ingest relay, which is a piece of infrastructure that does
    // not exist yet — say so, rather than leaving it looking stalled.
    const proto = s.ingest_protocol?.toUpperCase() ?? 'RTMP';
    return s.status === 'live'
      ? `${proto} ingest — receiving`
      : `${proto} ingest — waiting for the relay, which is not live yet`;
  }
  if (s.status !== 'ready') return s.error ?? '—';
  const bits: string[] = [];
  if (s.width && s.height) bits.push(`${s.width}×${s.height}`);
  if (s.interlaced) bits.push('interlaced → deinterlaced');
  if (s.duration_ms) bits.push(`${(s.duration_ms / 1000).toFixed(1)}s`);
  bits.push(s.has_audio ? 'audio' : 'silent');
  return bits.join(' · ');
}

/** What the speech-to-text service has to say about a source, in a word. */
function transcriptNote(s: Source): string | null {
  switch (s.transcript_status) {
    case 'transcribing': return 'transcribing…';
    case 'ready': return 'transcribed';
    case 'error': return `transcription failed: ${s.transcript_error ?? 'no reason recorded'}`;
    default: return null;
  }
}

export function Sources({ brand, onOpenCard }: { brand: Brand | null; onOpenCard?: (id: string) => void }) {
  const [cards, setCards] = useState<Card[]>([]);
  useEffect(() => { void api.cards().then((r) => setCards(r.cards)).catch(() => undefined); }, []);
  const [sources, setSources] = useState<Source[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // T238: the name the director would give a take, offered beside the take's own until taken or dismissed.
  const [suggested, setSuggested] = useState<Record<string, string>>({});
  // Whether this deployment can transcribe at all. Read once; the button is
  // disabled with the reason rather than hidden, as every control that maps
  // to something the render plane may not have is.
  const [canTranscribe, setCanTranscribe] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    api.capabilities()
      .then((c) => { if (live) setCanTranscribe(Boolean(c.transcription)); })
      .catch(() => { if (live) setCanTranscribe(false); });
    return () => { live = false; };
  }, []);
  const [kind, setKind] = useState<SourceKind>('file');
  const [name, setName] = useState('');
  const [streamKey, setStreamKey] = useState<{ name: string; key: string; url: string } | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const poll = useRef<number | null>(null);

  const chosen = KINDS.find((k) => k.id === kind)!;

  async function load() {
    try { setSources((await api.sources(brand?.id)).sources); }
    catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [brand?.id]);

  // Only while something is actually being ingested or transcribed.
  useEffect(() => {
    const live = sources.some((s) =>
      s.status === 'ingesting' || s.status === 'pending' || s.transcript_status === 'transcribing');
    if (!live) { if (poll.current) { window.clearInterval(poll.current); poll.current = null; } return; }
    if (poll.current) return;
    poll.current = window.setInterval(() => void load(), 6000);
    return () => { if (poll.current) { window.clearInterval(poll.current); poll.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources.map((s) => `${s.status}/${s.transcript_status ?? ''}`).join()]);

  async function add() {
    const picked = file.current?.files?.[0];
    if (!chosen.live && !picked) { setError('choose a file to upload'); return; }
    setBusy('add'); setError(null);
    try {
      const created = await api.createSource({
        name: name.trim() || picked?.name || chosen.label,
        kind, brandId: brand?.id ?? null,
        mode: chosen.live ? 'live' : 'stored',
        ...(picked ? { filename: picked.name, contentType: picked.type || 'video/mp4' } : {}),
      });

      if (created.streamKey) {
        // Shown once. A replacement is a new key, not a lookup.
        setStreamKey({
          name: created.source.name,
          key: created.streamKey,
          url: created.source.ingest_url ?? '',
        });
      }

      if (picked && created.upload) {
        // Straight to GCS. The bytes never pass through the API, which is what
        // makes a two-gigabyte tape capture a non-event.
        const put = await fetch(created.upload.url, {
          method: 'PUT',
          headers: { 'Content-Type': picked.type || 'video/mp4' },
          body: picked,
        });
        if (!put.ok) throw new Error(`upload failed (${put.status})`);
        await api.ingestSource(created.source.id);
      }

      setName(''); if (file.current) file.current.value = '';
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <>
      <div className="vhead" data-move="source-failed">
        <div>
          <h2>Sources</h2>
          <p>
            Cameras, audio feeds, screen shares, and footage from a file, a tape or a disc.
            Everything is probed on arrival and conformed to one house format, so the timeline
            cuts to a VHS capture as easily as to a generated panel.
          </p>
        </div>
      </div>

      {error && <div className="err">{error}</div>}

      {streamKey && (
        <div className="panel" style={{ borderColor: 'var(--amber)', marginBottom: 14 }}>
          <div className="panel-hd">
            <h3>Stream key for “{streamKey.name}”</h3>
            <button className="btn" onClick={() => setStreamKey(null)}>Done</button>
          </div>
          <div className="panel-bd">
            <p className="hint" style={{ marginBottom: 8 }}>
              Copy this now — it is encrypted at rest and cannot be shown again.
            </p>
            <code className="keyline">{streamKey.url}</code>
            <code className="keyline">{streamKey.key}</code>
          </div>
        </div>
      )}

      {/* The cameras plugged into this machine, live, each one a take away
          from being a source — and from there a scene on the timeline. */}
      <CameraCapture brand={brand} onCaptured={() => void load()} onError={setError} />

      <div className="panel">
        <div className="panel-hd"><h3>Add a source</h3></div>
        <div className="panel-bd">
          <div className="kinds">
            {KINDS.map((k) => (
              <button key={k.id} className={`kind${k.id === kind ? ' on' : ''}`} onClick={() => setKind(k.id)}>
                <b>{k.label}</b><i>{k.hint}</i>
              </button>
            ))}
          </div>
          <div className="row wrap" style={{ marginTop: 12 }}>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Name it — e.g. Shop floor B-roll" style={{ flex: '1 1 220px' }} />
            {!chosen.live && <input ref={file} type="file" accept="video/*,audio/*" />}
            <button className="btn pri" disabled={busy === 'add'} onClick={() => void add()}>
              {busy === 'add' ? 'Adding…' : chosen.live ? 'Create ingest' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-hd"><h3>Library</h3><span className="chip">{sources.length}</span></div>
        {sources.length === 0 ? (
          <div className="empty">Nothing yet. Upload footage or create a live ingest above.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>Kind</th><th>Status</th><th>Detail</th><th>Shelf</th><th /></tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id}>
                  {/* PATCH /v1/sources/:id accepts a rename and nothing called
                      it, so a source named in haste during an upload was named
                      that forever. Click the name to fix it. */}
                  <td>
                    <b
                      title="Click to rename"
                      style={{ cursor: 'pointer', borderBottom: '1px dashed var(--line-2)' }}
                      onClick={() => {
                        const next = window.prompt('Rename this source', s.name);
                        if (next === null) return;
                        const trimmed = next.trim();
                        if (!trimmed || trimmed === s.name) return;
                        void api.renameSource(s.id, trimmed)
                          .then(load)
                          .catch((e) => setError((e as Error).message));
                      }}
                    >{s.name}</b>
                  </td>
                  <td style={{ color: 'var(--ink-2)' }}>{KINDS.find((k) => k.id === s.kind)?.label ?? s.kind}</td>
                  <td style={{ color: STATUS_TONE[s.status], fontWeight: 500 }}>{s.status}</td>
                  {/* The shelf: the working set the assistant director reaches
                      for by default. Everything else is the vault — still here,
                      still usable by hand, offered to the director only when
                      asked to range wider. For six clips this is nothing; for a
                      thousand it is the difference between a useful suggestion
                      and a scroll through footage nobody has watched in a year.

                      A still ingesting source is not offerable either way, so
                      the control is disabled rather than shown and ignored. */}
                  <td style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>{describe(s)}{transcriptNote(s) && (<span style={{ color: s.transcript_status === 'error' ? 'var(--red)' : 'var(--ink-2)' }}>{' · '}{transcriptNote(s)}</span>)}{s.summary && <div data-tool="take-summary" style={{ color: 'var(--ink-2)', marginTop: 2 }}>{s.summary}</div>}{suggested[s.id] && <div className="row" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}><span>The director would call it <b>{suggested[s.id]}</b></span><button type="button" className="btn tiny pri" onClick={() => { const n = suggested[s.id]!; setSuggested((m) => { const { [s.id]: _, ...rest } = m; return rest; }); void api.renameSource(s.id, n).then(load).catch((e) => setError((e as Error).message)); }}>Use it</button><button type="button" className="btn tiny" onClick={() => setSuggested((m) => { const { [s.id]: _, ...rest } = m; return rest; })}>Keep mine</button></div>}</td>
                  <td>
                    <button
                      className={`btn tiny${s.on_shelf === false ? '' : ' pri'}`}
                      disabled={s.status !== 'ready'}
                      title={s.status !== 'ready'
                        ? 'Only a ready source can be on the shelf'
                        : s.on_shelf === false
                          ? 'In the vault — the director reaches for it only when asked to range wider'
                          : 'On the shelf — the director reaches for this by default'}
                      onClick={() => void api.shelveSource(s.id, s.on_shelf === false)
                        .then(load)
                        .catch((e) => setError((e as Error).message))}
                    >
                      {s.on_shelf === false ? 'Vault' : 'Shelf'}
                    </button>
                  </td>
                  <td className="r">
                    {/* Listen to the recording and write down what is said,
                        with times. What comes back becomes scenes from the
                        card ("Scenes from a recording"), each playing its
                        own stretch with its own sound. Offered on a ready
                        source with sound; disabled with the reason when this
                        deployment has no speech-to-text configured. */}
                    {s.mode === 'stored' && s.has_audio !== false && s.transcript_status !== 'transcribing' && (
                      <button
                        className="btn tiny"
                        disabled={s.status !== 'ready' || canTranscribe === false || busy === s.id}
                        title={canTranscribe === false
                          ? 'Transcription is not configured on this deployment'
                          : s.status !== 'ready'
                            ? 'Only a ready source can be transcribed'
                            : s.transcript_status === 'ready'
                              ? 'Transcribe it again'
                              : 'Listen to the recording and write down what is said, with times'}
                        onClick={() => {
                          setBusy(s.id); setError(null);
                          void api.transcribeSource(s.id)
                            .then(load)
                            .catch((e) => setError((e as Error).message))
                            .finally(() => setBusy(null));
                        }}
                      >
                        {s.transcript_status === 'ready' ? 'Transcribe again' : 'Transcribe'}
                      </button>
                    )}
                    {s.status === 'error' && (
                      <button className="btn tiny" title="Try ingesting again"
                              onClick={() => void api.ingestSource(s.id).then(load).catch((e) => setError(e.message))}>↻</button>
                    )}
                    {s.mode === 'stored' && s.duration_ms != null && s.duration_ms > 0 && (
                      <>
                        <SourceThumb source={s} width={72} />
                        <SourcePlayer source={s} onError={setError} />
                      </>
                    )}
                    {s.mode === 'stored' && s.status === 'ready' && s.has_words && (
                      <button className="btn tiny" data-tool="read-take" disabled={busy === s.id} title="The director reads what was said: a name, a summary, chapters"
                              onClick={() => { setBusy(s.id); void api.readSource(s.id).then((r) => { if (r.title && r.title !== s.name) setSuggested((m) => ({ ...m, [s.id]: r.title })); return load(); }).catch((e) => setError((e as Error).message)).finally(() => setBusy(null)); }}>{busy === s.id ? 'Reading…' : s.read_at ? 'Read again' : 'Read'}</button>
                    )}
                    {(s.kind === 'camera' || s.kind === 'screen') && s.mode === 'stored' && (
                      <AddTakeToVideo take={s} said={s.said ?? ''} brand={brand} cards={cards} onOpenCard={onOpenCard} onError={setError} />
                    )}
                    {(s.kind === 'screen' || s.kind === 'camera') && s.mode === 'stored' && s.status === 'ready' && (s.has_words || (s.chapters?.length ?? 0) > 0) && (
                      <DemoChapters source={s} brand={brand} onOpenCard={onOpenCard} onError={setError} onChapters={(ch) => setSources((prev) => prev.map((x) => (x.id === s.id ? { ...x, chapters: ch } : x)))} />
                    )}
                    {s.status === 'ready' && s.has_audio && (
                      <button className="btn tiny" title="The sound on its own, as a wav"
                              onClick={() => void api.sourceAudio(s.id).then((a) => window.open(a.url, '_blank', 'noopener')).catch((e) => setError(e.message))}>♪</button>
                    )}
                    <button className="btn tiny" title="Remove"
                            onClick={() => void api.deleteSource(s.id).then(load).catch((e) => setError(e.message))}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}


/**
 * SN3: chapters on a demonstration, and an episode cut from them. The director
 * reads the take's words (with their times) and marks the chapters; each
 * chapter becomes a scene cut to the take with its own sound and its words as
 * the caption line — a ten-minute walkthrough is an episode in two clicks.
 */
export function DemoChapters({ source, brand, onOpenCard, onError, onChapters }: {
  source: Source; brand: Brand | null; onOpenCard?: (id: string) => void; onError: (m: string) => void; onChapters: (ch: DemoChapter[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const chapters = source.chapters ?? [];
  const mmss = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

  async function mark() {
    setBusy('marking');
    try { onChapters(await api.sourceChapters(source.id)); setOpen(true); }
    catch (e) { onError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function episode() {
    setBusy('cutting');
    try {
      const speaker = Object.keys(brand?.voices ?? {})[0] ?? 'Host';
      onOpenCard?.(await episodeFromTake({ sourceId: source.id, name: source.name, brandId: brand?.id ?? null, speaker, chapters }));
    } catch (e) { onError((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <span data-tool="demo-chapters" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {chapters.length === 0
        ? <button className="btn tiny" disabled={!!busy} title="The director reads what was said and marks where each step begins" onClick={() => void mark()}>{busy === 'marking' ? 'Marking…' : 'Chapters'}</button>
        : <button className="btn tiny" onClick={() => setOpen(!open)} title="The chapters the director marked">{chapters.length} chapters</button>}
      {chapters.length > 0 && <button className="btn tiny pri" disabled={!!busy} title="A card with one scene per chapter, cut to this take with its own sound and its words as the line" onClick={() => void episode()}>{busy === 'cutting' ? 'Cutting…' : 'Make an episode'}</button>}
      {open && chapters.length > 0 && (
        <ol style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, flexBasis: '100%' }}>
          {chapters.map((c) => <li key={c.at}><code>{mmss(c.at)}</code> {c.title}</li>)}
          <li style={{ listStyle: 'none', marginLeft: -18 }}><button className="btn tiny" disabled={!!busy} onClick={() => void mark()}>mark again</button></li>
        </ol>
      )}
    </span>
  );
}
